import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Sparkles, ArrowRight, Loader2, Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const INTERESTS = [
  "Engineering & Technology", "Computer Science / AI / Data Science", "Business & Management",
  "Commerce & Finance", "Medicine & Healthcare", "Law", "Design & Architecture",
  "Media & Communication", "Humanities & Social Sciences", "Science",
  "Hospitality & Tourism", "Education", "Agriculture", "Other",
];

const PRIORITIES = [
  "Affordable fees", "Career opportunities", "Placement opportunities", "College reputation",
  "Academic quality", "International exposure", "Location", "Campus life",
  "Research opportunities", "Flexible learning",
];

const LEVELS = ["Class 10","Class 11","Class 12","Diploma","Undergraduate","Graduate","Postgraduate","Working Professional","Other"];
const BUDGETS = ["Below ₹1 lakh","₹1–3 lakh","₹3–5 lakh","₹5–10 lakh","₹10–20 lakh","Above ₹20 lakh","Not sure"];
const LOCATIONS = ["Anywhere in India","My city","My state","Specific city","Abroad","India or Abroad"];
const MODES = ["On Campus","Online","Hybrid","No preference"];

const INPUT = "w-full px-4 py-2.5 rounded-xl border border-slate-200 outline-none focus:border-sky-400 text-sm bg-white";

const CardOption = ({ selected, onClick, children, testId }) => (
  <button type="button" onClick={onClick}
    className={`px-4 py-2.5 rounded-full text-sm font-semibold border transition-colors text-left ${selected ? "bg-slate-900 text-yellow-300 border-slate-900" : "bg-white text-slate-700 border-slate-200 hover:border-sky-400"}`}
    data-testid={testId}>{children}</button>
);

export default function AiAdvisorPage() {
  const nav = useNavigate();
  const [intro, setIntro] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [step, setStep] = useState("landing"); // landing | form | submitting
  const [busy, setBusy] = useState(false);

  const [f, setF] = useState({
    academic_level: "", percentage: "", interests: [], career_goal: "", course_considered: "",
    budget: "", location_pref: "", location_detail: "", mode: "", priorities: [], notes: "",
    consent: false,
  });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));
  const toggle = (k, val) => setF(p => ({ ...p, [k]: p[k].includes(val) ? p[k].filter(x => x !== val) : [...p[k], val] }));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
    api.get("/advisor/settings").then(r => {
      setEnabled(r.data.enabled);
      setIntro(r.data.intro_text || "");
    }).catch(() => {});
  }, []);

  // When step transitions to "submitting", scroll to top so the loader is visible.
  useEffect(() => {
    if (step === "submitting" || step === "form") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [step]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.academic_level) { toast.error("Please select your academic level"); return; }
    if (!f.interests.length) { toast.error("Please select at least one interest"); return; }
    if (!f.consent) { toast.error("Please accept the privacy notice to continue"); return; }
    setBusy(true); setStep("submitting");
    try {
      const { data } = await api.post("/advisor/sessions", f);
      nav(`/ai-advisor/report/${data.session_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
      setStep("form");
    } finally { setBusy(false); }
  };

  if (!enabled) {
    return <div className="max-w-2xl mx-auto px-4 py-24 text-center text-slate-500">
      AI Advisor is currently unavailable. Please check back soon.
    </div>;
  }

  if (step === "submitting") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="advisor-submitting">
        <Loader2 className="w-10 h-10 animate-spin mx-auto text-sky-700 mb-6"/>
        <h1 className="font-editorial text-3xl font-bold text-slate-900 mb-2">EduTime AI is researching your options…</h1>
        <p className="text-slate-600">This usually takes 10–30 seconds. We're checking current course, fee and eligibility data.</p>
      </div>
    );
  }

  if (step === "landing") {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16" data-testid="advisor-landing">
        <div className="flex items-center gap-2 mb-4">
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ background:"#F0C948", color:"#0F172A" }}>
            <Sparkles className="w-3 h-3"/> New · AI-Powered
          </span>
        </div>
        <div className="overline text-sky-700 mb-2">EDUTIME AI EDUCATION ADVISOR</div>
        <h1 className="font-editorial text-4xl md:text-6xl font-black text-slate-900 leading-[1.05] mb-4">
          Find the Right Course. <br/>Choose the Right College. <br/>Plan Your Future.
        </h1>
        <p className="text-slate-600 text-lg max-w-2xl mt-4">{intro}</p>
        <button onClick={() => setStep("form")}
          className="mt-8 inline-flex items-center gap-2 px-7 py-3.5 rounded-full text-base font-bold shadow-md hover:-translate-y-0.5 transition-transform"
          style={{ background:"#0F172A", color:"#F0C948" }}
          data-testid="advisor-start-btn">
          Start Your AI Advisory <ArrowRight className="w-4 h-4"/>
        </button>
        <p className="text-xs text-slate-500 mt-4">Takes ~2 minutes · Free · No signup required</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="max-w-3xl mx-auto px-4 py-10 space-y-7" data-testid="advisor-form">
      <div className="overline text-sky-700 mb-2">EDUTIME AI ADVISOR · STEP 1 OF 1</div>
      <h1 className="font-editorial text-3xl md:text-4xl font-bold text-slate-900 leading-tight">Tell us about you</h1>

      {/* Q1 */}
      <div>
        <div className="text-sm font-bold text-slate-800 mb-2">1. Current level of education</div>
        <div className="flex flex-wrap gap-2">
          {LEVELS.map(l => <CardOption key={l} selected={f.academic_level === l} onClick={() => set("academic_level", l)} testId={`adv-level-${l}`}>{l}</CardOption>)}
        </div>
      </div>

      {/* Q2 */}
      <label>
        <div className="text-sm font-bold text-slate-800 mb-2">2. Latest percentage / CGPA</div>
        <input value={f.percentage} onChange={e => set("percentage", e.target.value)} placeholder="e.g. 82% or 8.4 CGPA" className={INPUT} data-testid="adv-percentage"/>
      </label>

      {/* Q3 */}
      <div>
        <div className="text-sm font-bold text-slate-800 mb-2">3. Interests <span className="text-slate-500 font-normal">(pick one or more)</span></div>
        <div className="flex flex-wrap gap-2">
          {INTERESTS.map(i => <CardOption key={i} selected={f.interests.includes(i)} onClick={() => toggle("interests", i)} testId={`adv-interest-${i}`}>{i}</CardOption>)}
        </div>
      </div>

      {/* Q4 */}
      <label>
        <div className="text-sm font-bold text-slate-800 mb-2">4. What career or profession interests you?</div>
        <input value={f.career_goal} onChange={e => set("career_goal", e.target.value)} placeholder='e.g. "I want to become a financial analyst"' className={INPUT} data-testid="adv-career"/>
      </label>

      {/* Q5 */}
      <label>
        <div className="text-sm font-bold text-slate-800 mb-2">5. What course are you considering? <span className="text-slate-500 font-normal">(optional)</span></div>
        <input value={f.course_considered} onChange={e => set("course_considered", e.target.value)} placeholder="e.g. B.Tech CSE, MBA Finance" className={INPUT} data-testid="adv-course"/>
      </label>

      {/* Q6 */}
      <div>
        <div className="text-sm font-bold text-slate-800 mb-2">6. Approximate annual education budget</div>
        <div className="flex flex-wrap gap-2">
          {BUDGETS.map(b => <CardOption key={b} selected={f.budget === b} onClick={() => set("budget", b)} testId={`adv-budget-${b}`}>{b}</CardOption>)}
        </div>
      </div>

      {/* Q7 */}
      <div>
        <div className="text-sm font-bold text-slate-800 mb-2">7. Where would you prefer to study?</div>
        <div className="flex flex-wrap gap-2 mb-2">
          {LOCATIONS.map(l => <CardOption key={l} selected={f.location_pref === l} onClick={() => set("location_pref", l)} testId={`adv-loc-${l}`}>{l}</CardOption>)}
        </div>
        {(f.location_pref === "My city" || f.location_pref === "My state" || f.location_pref === "Specific city" || f.location_pref === "Abroad") && (
          <input value={f.location_detail} onChange={e => set("location_detail", e.target.value)} placeholder="Which city / state / country?" className={INPUT} data-testid="adv-loc-detail"/>
        )}
      </div>

      {/* Q8 */}
      <div>
        <div className="text-sm font-bold text-slate-800 mb-2">8. Preferred mode</div>
        <div className="flex flex-wrap gap-2">
          {MODES.map(m => <CardOption key={m} selected={f.mode === m} onClick={() => set("mode", m)} testId={`adv-mode-${m}`}>{m}</CardOption>)}
        </div>
      </div>

      {/* Q9 */}
      <div>
        <div className="text-sm font-bold text-slate-800 mb-2">9. What matters most to you? <span className="text-slate-500 font-normal">(pick a few)</span></div>
        <div className="flex flex-wrap gap-2">
          {PRIORITIES.map(p => <CardOption key={p} selected={f.priorities.includes(p)} onClick={() => toggle("priorities", p)} testId={`adv-priority-${p}`}>{p}</CardOption>)}
        </div>
      </div>

      {/* Q10 */}
      <label>
        <div className="text-sm font-bold text-slate-800 mb-2">10. Anything else you want the AI Advisor to know? <span className="text-slate-500 font-normal">(optional)</span></div>
        <textarea rows={3} value={f.notes} onChange={e => set("notes", e.target.value)} className={`${INPUT} min-h-[80px]`} data-testid="adv-notes"/>
      </label>

      {/* Consent */}
      <label className="flex items-start gap-2 text-sm text-slate-700 p-4 rounded-2xl bg-slate-50 border border-slate-200">
        <input type="checkbox" checked={!!f.consent} onChange={e => set("consent", e.target.checked)} className="mt-1" data-testid="adv-consent"/>
        <span>
          <ShieldCheck className="w-4 h-4 inline mr-1 text-emerald-700"/>
          I agree to EduTime processing my answers to generate AI recommendations. My answers are stored anonymously to improve the service.
        </span>
      </label>

      <button type="submit" disabled={busy}
        className="w-full sm:w-auto inline-flex items-center gap-2 px-8 py-3.5 rounded-full text-base font-bold shadow-md hover:-translate-y-0.5 transition-transform disabled:opacity-60"
        style={{ background:"#0F172A", color:"#F0C948" }}
        data-testid="adv-submit">
        {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Generating…</> : <>Get My AI Recommendations <ArrowRight className="w-4 h-4"/></>}
      </button>
    </form>
  );
}
